import { atom, map } from "nanostores";

export type CursorPaginationStoreState = {
  currentPageIndex: number; // Current page index (0 = page 1)
  nextCursor: null | string; // Cursor for next page from API response
  visitedCursors: (null | string)[]; // Array of all cursors we've seen, in order. Index 0 = page 1 (null), index 1 = page 2, etc.
};

export interface CursorPaginationStore {
  $cursorParams: ReturnType<typeof atom<null | string>>;
  $pagination: ReturnType<typeof map<CursorPaginationStoreState>>;
  goToPage: (pageIndex: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  reset: () => void;
  setNextCursor: (cursor: null | string) => void;
}

export const createCursorPaginationStore = (): CursorPaginationStore => {
  // Start with page 1 (null cursor) in the visited cursors array
  const $pagination = map<CursorPaginationStoreState>({
    currentPageIndex: 0,
    nextCursor: null,
    visitedCursors: [null],
  });

  const $cursorParams = atom<null | string>(null);

  $pagination.listen((state) => {
    const currentCursor = state.visitedCursors[state.currentPageIndex] ?? null;
    $cursorParams.set(currentCursor);
  });

  // Actions to manage pagination
  const nextPage = (): void => {
    const state = $pagination.get();
    const { currentPageIndex, nextCursor, visitedCursors } = state;

    // Check if we've already visited the next page
    const nextPageIndex = currentPageIndex + 1;
    if (nextPageIndex < visitedCursors.length) {
      // We've been here before - just move to the next page
      $pagination.set({
        currentPageIndex: nextPageIndex,
        nextCursor: visitedCursors[nextPageIndex] ?? null,
        visitedCursors,
      });
    } else if (nextCursor) {
      // We're moving to a new page - add the cursor to visitedCursors
      const newVisitedCursors = [...visitedCursors, nextCursor];
      $pagination.set({
        currentPageIndex: nextPageIndex,
        nextCursor: null, // Will be set by API response
        visitedCursors: newVisitedCursors,
      });
    }
  };

  const prevPage = (): void => {
    const state = $pagination.get();
    const { currentPageIndex, visitedCursors } = state;

    if (currentPageIndex > 0) {
      // Go back to previous page
      $pagination.set({
        currentPageIndex: currentPageIndex - 1,
        nextCursor:
          currentPageIndex < visitedCursors.length
            ? visitedCursors[currentPageIndex]
            : null,
        visitedCursors,
      });
    }
    // If currentPageIndex is 0, we're already at the first page, do nothing
  };

  const goToPage = (pageIndex: number): void => {
    const state = $pagination.get();
    const { visitedCursors } = state;

    // Only allow going to pages we've already visited
    if (pageIndex >= 0 && pageIndex < visitedCursors.length) {
      $pagination.set({
        currentPageIndex: pageIndex,
        nextCursor: visitedCursors[pageIndex + 1] ?? null,
        visitedCursors,
      });
    }
  };

  const setNextCursor = (cursor: null | string): void => {
    $pagination.setKey("nextCursor", cursor);
  };

  const reset = (): void => {
    $pagination.set({
      currentPageIndex: 0,
      nextCursor: null,
      visitedCursors: [null], // Reset to page 1
    });
  };

  return {
    $cursorParams,
    $pagination,
    goToPage,
    nextPage,
    prevPage,
    reset,
    setNextCursor,
  };
};
